import { and, count, eq, list, type PowerSyncDrizzleDB, sql, todo } from "@cartero/powersync";
import { liveListFilters, liveTodoFilters } from "./filters";

/**
 * Live global search (⌘K command palette). It used to live inline in
 * `command-palette.tsx` with `.from(todo)`/`.from(list)` and NO trash filter
 * → search showed deleted tasks/lists (a scattered invariant bug).
 * Migrated to the live layer: it now composes `liveTodoFilters()`/`liveListFilters()`.
 */
export type SearchFilter = "all" | "todo" | "list";

export interface GlobalSearchOptions {
  query: string;
  filter: SearchFilter;
  limit?: number;
}

const DEFAULT_SEARCH_LIMIT = 30;

export function getGlobalSearchQuery(
  db: PowerSyncDrizzleDB,
  { query, filter, limit = DEFAULT_SEARCH_LIMIT }: GlobalSearchOptions,
) {
  const escaped = `%${query}%`;

  const todoQ = db
    .select({
      type: sql<"todo" | "list">`'todo'`.as("type"),
      id: todo.id,
      text: todo.text,
      completed: todo.completed,
      color: sql<string | null>`NULL`.as("color"),
      icon: sql<string | null>`NULL`.as("icon"),
      listId: todo.list_id,
    })
    .from(todo)
    .where(and(sql`${todo.text} LIKE ${escaped}`, ...liveTodoFilters()));

  const listQ = db
    .select({
      type: sql<"todo" | "list">`'list'`.as("type"),
      id: list.id,
      text: list.name,
      completed: sql<number | null>`NULL`.as("completed"),
      color: list.color,
      icon: list.icon,
      listId: sql<string | null>`NULL`.as("list_id"),
    })
    .from(list)
    .where(and(sql`${list.name} LIKE ${escaped}`, ...liveListFilters()));

  if (filter === "todo") {
    return db.select().from(todoQ.as("search_results")).limit(limit);
  }
  if (filter === "list") {
    return db.select().from(listQ.as("search_results")).limit(limit);
  }
  return db.select().from(todoQ.unionAll(listQ).as("search_results")).limit(limit);
}

/**
 * Counts a user's LIVE tasks. It feeds `checkUnsyncedActor` (the
 * "keep local data?" prompt when migrating from guest to a real account): it used
 * to do a raw `count() from(todo)`, counting trash items too. Through the live
 * layer it only counts live tasks — consistent with the other views. (The guest→real
 * merge still runs unconditionally, so no rows are lost; this counter only decides
 * whether the prompt is SHOWN.)
 */
export function getLiveTodoCountForUserQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({ total: count() })
    .from(todo)
    .where(and(eq(todo.user_id, userId), ...liveTodoFilters()));
}
